import { aws } from '../config';

// BSA API URLs
const BSA_DEV_URL = aws.devOddApiGateway.URL;
const BSA_PROD_URL = aws.oddApiGateway.URL;

// Module-level state for tenant bsaProd setting
let _bsaProdSetting = null; // null = not fetched, true = use prod, false = use dev
let _bsaProdInitialized = false;

/**
 * Initialize the bsaProd tenant setting.
 * Must be called before any ODD API calls.
 * @param {boolean|null} bsaProdValue - true = force prod, false = allow dev, null = default to prod
 */
export const initBsaProdSetting = bsaProdValue => {
  _bsaProdSetting = bsaProdValue;
  _bsaProdInitialized = true;
};

/**
 * Check if the tenant setting forces BSA to use production ODD.
 * Returns true if bsaProd setting is true or not yet initialized (default to prod).
 * @returns {boolean}
 */
export const isBsaProdForced = () => {
  if (!_bsaProdInitialized) return true; // Default: force prod until setting is known
  return _bsaProdSetting !== false; // true or null both mean "use prod"
};

/**
 * Reset bsaProd setting state (for testing).
 */
export const resetBsaProdSetting = () => {
  _bsaProdSetting = null;
  _bsaProdInitialized = false;
};

/**
 * Check if the current environment is development
 * @returns {boolean}
 */
export const isDevelopmentEnvironment = () => {
  return process.env.NODE_ENV === 'development' || process.env.REACT_APP_BUILD_ENV === 'development';
};

/**
 * Get available BSA environments
 * @returns {Array} Array of environment options
 */
export const getBsaEnvironments = () => [
  { value: 'dev', label: 'BSA Dev', url: BSA_DEV_URL },
  { value: 'prod', label: 'BSA Prod', url: BSA_PROD_URL }
];

/**
 * Get the current BSA environment
 * @returns {string} Current BSA environment ('dev' or 'prod')
 */
export const getCurrentBsaEnvironment = () => {
  // Always follow the bsaProd tenant setting (defaults to prod if not initialized)
  return isBsaProdForced() ? 'prod' : 'dev';
};

/**
 * Get the current BSA API URL based on the selected environment
 * @returns {string} The BSA API URL
 */
export const getBsaApiUrl = () => {
  const environment = getCurrentBsaEnvironment();
  const environments = getBsaEnvironments();
  const selected = environments.find(env => env.value === environment);
  return selected ? selected.url : BSA_DEV_URL;
};

/**
 * Get the current BSA environment label for display
 * @returns {string} The display label for the current environment
 */
export const getCurrentBsaEnvironmentLabel = () => {
  // Display label for the header pill — just "Prod"/"Dev" (no "BSA" prefix).
  return getCurrentBsaEnvironment() === 'prod' ? 'Prod' : 'Dev';
};
